import classNames from 'classnames';
import { useAtom } from 'jotai';
import React, { useMemo, useEffect, useRef, useState } from 'react';
import { isSafari, isMobile } from 'react-device-detect';
import ReactGA from 'react-ga4';
import { useTranslation } from 'react-i18next';

import { squareTriangleFullBgMp4, squareTriangleLoopBgMp4 } from '../../assets/videos';
import { DefaultHeader } from '../../components/DefaultHeader';
import { EmbedCountdown } from '../../components/EmbedCountdown';
import { FindLiveClassButton } from '../../components/FindLiveClassButton';
import { FullScreenModalReservation } from '../../components/FullScreenModalReservation';
import { LiveClassesProgressBar } from '../../components/LiveClassesProgressBar';
import { ModalTimezone } from '../../components/ModalTimezone';
import { UnitsProgressBar } from '../../components/UnitsProgressBar';
import { VideoWithLoop } from '../../components/VideoWithLoop';
import { SITE_LINKS } from '../../constants';
import { GoogleAnalyticsEvents } from '../../enum/GoogleAnalyticsEvents';
import { useDimension } from '../../hooks/use-dimension';
import { useGetLevel } from '../../hooks/use-get-level';
import { useGetToday } from '../../hooks/use-get-today';
import { reservationDataAtom, ReservationModal } from '../../jotai-atoms/reservation-data.atom';
import { userAtom } from '../../jotai-atoms/user.atom';
import { getUnitListItemStatus } from '../../models/LevelPageUnit';
import { Background } from '../../stories/Background';
import { Link } from '../../stories/Link';
import { ModalPlatformTourFeedbackPending } from '../../stories/ModalPlatformTourFeedbackPending';
import { ModalPlatformTourVideo } from '../../stories/ModalPlatformTourVideo';
import { ModalPlay2LearnInvitation } from '../../stories/ModalPlay2LearnInvitation';
import { ModalReservation } from '../../stories/ModalReservation';
import { ModalReservationNoCredits } from '../../stories/ModalReservationNoCredits';
import { ModalReservationReserve } from '../../stories/ModalReservationReserve';
import { NotificationPlay2LearnInvitation } from '../../stories/NotificationPlay2LearnInvitation';
import { Tag } from '../../stories/Tag';
import { UnitList } from '../../stories/UnitList';
import { UnitListItemProps, UnitListItemStatusType } from '../../stories/UnitListItem';
import { scrollToTop } from '../../utils';
import { ErrorPage } from '../Error/Error';

type PageProps = {
  csrftoken: string;
};

export const LevelPage = (props: PageProps) => {
  const __ = useTranslation().t;
  let levelPosition = null;
  const pathParts = window.location.pathname.split('/');
  const levelIndex = pathParts.indexOf('level');
  if (levelIndex !== -1 && pathParts[levelIndex + 1]) {
    levelPosition = pathParts[levelIndex + 1];
  }
  const { data: levelData, isError } = useGetLevel(levelPosition);
  const { today } = useGetToday();

  const currentLevel = levelData?.position;

  const numberOfCompletedUnits = levelData?.completedUnits || 0;
  const totalUnits = levelData?.totalUnits || 1;
  const completedUnitsPercentage = (100 * numberOfCompletedUnits) / totalUnits;

  const numberOfCompletedLiveClasses = levelData?.completedLiveClasses || 0;
  const totalLiveClasses = levelData?.requiredLiveClasses || 1;
  const completedLiveClassesPercentage = (100 * numberOfCompletedLiveClasses) / totalLiveClasses;

  const [reservationData, setReservationData] = useAtom(reservationDataAtom);
  const [user] = useAtom(userAtom);

  const timezoneCallbackRef = useRef<null | (() => void)>(null);
  const { width } = useDimension('window');
  const isDesktop = width >= 1200;

  const shouldShowP2LInvitation = !!user?.isGameClassKahootBeta;
  const [showModalP2LInvitation, setShowModalP2LInvitation] = useState(shouldShowP2LInvitation);

  const activeModal = reservationData?.activeModal;
  const showFindLiveClassButton = !user?.nextLiveClass && !shouldShowP2LInvitation;
  const hasNextLiveClass = !!user?.nextLiveClass;

  useEffect(() => {
    setShowModalP2LInvitation(shouldShowP2LInvitation);
  }, [shouldShowP2LInvitation]);

  useEffect(() => {
    setReservationData({
      ...reservationData,
      numberOfCompletedLiveClasses,
      totalLiveClasses,
    });
  }, []);

  useEffect(() => {
    localStorage.setItem('today', today || '');
  }, [today]);

  useEffect(() => {
    reservationData?.totalLiveClasses && checkOpenModalReservation();
  }, [reservationData?.totalLiveClasses]);

  const checkOpenModalReservation = () => {
    const openModalReservation = window.localStorage.getItem('openModalReservation') === '1';
    if (openModalReservation) {
      onFindLiveClassButtonClick(!isDesktop ? 'modalReservationXs' : 'modalReservationMd');
      window.localStorage.setItem('openModalReservation', '0');
    }
  };

  const setActiveModal = (modal: ReservationModal) => {
    setReservationData({
      ...reservationData,
      activeModal: modal,
    });
  };

  const onFindLiveClassButtonClick = (modalName: ReservationModal) => {
    if (!!user?.platformTourVideoPlayerUrl) {
      setActiveModal('modalPlatformTourVideo');
      return;
    }

    if (!user?.timezone) {
      setActiveModal('modalTimezone');
      timezoneCallbackRef.current = () => setActiveModal(modalName);
      return;
    }

    if (user.orientationClassTaken && !user?.unlockedOrientation) {
      setActiveModal('modalPlatformTourFeedbackPending');
      return;
    }

    ReactGA.event(GoogleAnalyticsEvents.BookClassOpen);
    scrollToTop();
    setActiveModal(modalName);
  };

  const handleCloseModal = () => {
    ReactGA.event(GoogleAnalyticsEvents.BookClassClose);
    setActiveModal(null);
  };

  const unitsData: UnitListItemProps[] | undefined = useMemo(() => {
    const unitsData: UnitListItemProps[] =
      levelData?.units?.map((unit) => {
        const listItemStatus: UnitListItemStatusType = getUnitListItemStatus(unit);

        const buttonText = listItemStatus === 'current' ? __('start') : undefined;

        return {
          ...unit,
          title: (
            <>
              <span>{unit.name}</span>
              <span className="d-inline">: {unit.description}</span>
            </>
          ),
          imgSrc: unit.thumbSrc,
          status: listItemStatus,
          buttonHref: SITE_LINKS.UnitPage(currentLevel, unit.position),
          buttonText,
        };
      }) || [];

    const eolTestAllowed = !!levelData?.eolTestAllowed;
    const levelStatus = levelData?.status;
    const endOfLevel: UnitListItemProps = {
      status: levelStatus === 'completed' ? 'done' : eolTestAllowed ? 'current' : 'blocked',
      buttonText: eolTestAllowed ? __('start') : undefined,
      title: __('endOfLevelTest'),
      buttonHref: SITE_LINKS.EndOfLevelTest(currentLevel),
      isEOL: true,
    };

    unitsData.push(endOfLevel);

    return unitsData;
  }, [levelData?.units, levelData?.eolTestAllowed]);

  const ProgressBars = () => (
    <>
      <UnitsProgressBar
        numberOfCompleted={numberOfCompletedUnits}
        total={totalUnits}
        percentage={completedUnitsPercentage}
      />
      <LiveClassesProgressBar
        numberOfCompleted={numberOfCompletedLiveClasses}
        total={totalLiveClasses}
        percentage={completedLiveClassesPercentage}
      />
    </>
  );

  if (isError) {
    return <ErrorPage data={{ needReload: true }} />;
  }

  return (
    <>
      <DefaultHeader activeItem={__('course')} />
      <Background type="partial">
        <div className="oejr-page-current-level">
          <div className="d-flex flex-column align-items-center d-xl-block" id="title-wrapper">
            <Tag shape="vertical-flag">{__('levelNumber', { 1: currentLevel })}</Tag>
            <Link
              href={SITE_LINKS.LevelsPage}
              icon="arrow-left"
              variant="black-main"
              italic={false}
            >
              {__('seeLevels')}
            </Link>
          </div>
          <div className="d-flex d-xl-none justify-content-center align-items-center progress-bars-wrapper">
            <ProgressBars />
          </div>
          {showFindLiveClassButton && (
            <div className="d-flex d-xl-none justify-content-center mt-5 mx-4">
              <FindLiveClassButton
                onClick={() => onFindLiveClassButtonClick('modalReservationXs')}
              />
            </div>
          )}
          {hasNextLiveClass && !isDesktop && <EmbedCountdown className="d-block d-xl-none" />}
          <div className="d-flex flex-column flex-xl-row">
            <div className="lesson-list">
              <div className="lesson-list-header">
                <Tag shape="vertical-label" variant="blue-main">
                  {__('units')}
                </Tag>
              </div>
              <UnitList units={unitsData || []} />
            </div>
            <div className="dashboard">
              <div className="d-none d-xl-flex justify-content-center align-items-center">
                <ProgressBars />
              </div>
              {hasNextLiveClass && isDesktop && (
                <EmbedCountdown className="d-none d-xl-block" isThin />
              )}
              {showFindLiveClassButton && (
                <div className="d-none d-xl-flex justify-content-center mt-5">
                  <FindLiveClassButton
                    onClick={() => onFindLiveClassButtonClick('modalReservationMd')}
                  />
                </div>
              )}
              <div className="text-center">
                <div
                  className={classNames(
                    hasNextLiveClass &&
                      (!reservationData?.widgetState ||
                        reservationData?.widgetState === 'class_later') &&
                      'd-inline-block d-xl-none',
                    'video-background-image',
                    isSafari && 'is-safari',
                    isMobile && 'is-mobile',
                  )}
                >
                  <VideoWithLoop
                    video1Mp4={squareTriangleFullBgMp4}
                    video2Mp4={squareTriangleLoopBgMp4}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Background>
      <div className="d-block d-sm-none">
        <FullScreenModalReservation
          show={activeModal === 'modalReservationXs'}
          onClose={handleCloseModal}
        />
      </div>
      <ModalReservation show={activeModal === 'modalReservationMd'} onClose={handleCloseModal} />
      <ModalReservationReserve
        show={activeModal === 'modalReservationReserve'}
        onClose={() => setActiveModal(null)}
        liveClass={reservationData?.selectedLiveClass}
      />
      <ModalReservationNoCredits
        show={activeModal === 'modalReservationNoCredits'}
        onClose={() => setActiveModal(null)}
      />
      <ModalTimezone
        show={activeModal === 'modalTimezone'}
        onClose={() => setActiveModal(null)}
        csrftoken={props.csrftoken}
        callback={timezoneCallbackRef.current}
      />
      <ModalPlatformTourFeedbackPending
        show={activeModal === 'modalPlatformTourFeedbackPending'}
        onClose={() => setActiveModal(null)}
      />
      <ModalPlay2LearnInvitation
        show={showModalP2LInvitation}
        onClose={() => setShowModalP2LInvitation(false)}
      />
      {!!user?.platformTourVideoPlayerUrl && (
        <ModalPlatformTourVideo
          show={activeModal === 'modalPlatformTourVideo'}
          onClose={handleCloseModal}
          url={user.platformTourVideoPlayerUrl}
        />
      )}
      <NotificationPlay2LearnInvitation show={shouldShowP2LInvitation && !showModalP2LInvitation} />
    </>
  );
};
